/**
 * The "Preview assessment" tab of scenario authoring — an AUTHORING/QA view
 * (owner decision: KEEP_AS_AUTHORIZED_AUTHORING_QA).
 *
 * Shows what an attempt at this scenario would be held to, per modality:
 * rubric and version, dimensions, whether Testing is available (and why not),
 * the evidence the modality requires and whether it exists today, and how the
 * result would present. It is never an attempt and shows no score: a dimension
 * is "scored from the attempt's evidence, or Not measured", an overall below
 * its coverage is UNKNOWN, and UNAVAILABLE evidence is never a 0.
 */

import type { AssessmentPreview, AssessmentPreviewModality } from '../../store/api/scenarioAuthoringApi';
import { reasonLabel } from './progressionTypes';

const MODALITY_LABELS: Record<string, string> = {
  text: 'Written Chat',
  realtime_audio: 'Voice',
  realtime_audio_video: 'Video',
};

const EVIDENCE_LABELS: Record<string, string> = {
  transcript_text: 'Transcript text',
  video_observable: 'Observable video evidence',
};

function ModalityCard({ m }: { m: AssessmentPreviewModality }) {
  return (
    <section className="rounded-lg border border-gray-200 p-4" data-testid={`preview-modality-${m.modality}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-gray-900">{MODALITY_LABELS[m.modality] ?? m.modality}</h3>
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${m.gradable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}
          data-testid={`preview-gradable-${m.modality}`}
        >
          {m.gradable ? 'Testing available' : `Testing unavailable${m.unavailableReason ? ` — ${m.unavailableReason.status}: ${reasonLabel(m.unavailableReason)}` : ''}`}
        </span>
      </div>
      {!m.offeredByServer && (
        <p className="mt-1 text-xs text-gray-500">This modality is not switched on on this server.</p>
      )}

      <p className="mt-2 text-sm text-gray-700">
        Rubric: {m.rubric ? <span>{m.rubric.title} <code className="text-xs">{m.rubric.id}@{m.rubric.version}</code></span> : <span className="text-gray-500">none applies</span>}
      </p>

      <div className="mt-2">
        <h4 className="text-xs font-semibold uppercase text-gray-500">Evidence required</h4>
        <ul className="mt-1 space-y-1 text-sm">
          {m.requiredEvidence.map((e) => (
            <li key={e.evidence} data-testid={`preview-evidence-${m.modality}-${e.evidence}`}>
              {EVIDENCE_LABELS[e.evidence] ?? e.evidence}:{' '}
              {e.state === 'available' ? 'available' : `UNAVAILABLE — ${e.explanation ?? e.reason}`}
              {m.evidenceNotJudged.includes(e.evidence) && ' (the rubric does not judge it)'}
            </li>
          ))}
        </ul>
      </div>

      {m.dimensions.length > 0 && (
        <div className="mt-2">
          <h4 className="text-xs font-semibold uppercase text-gray-500">Dimensions</h4>
          <ul className="mt-1 space-y-1 text-sm">
            {m.dimensions.map((d) => (
              <li key={d.key}>
                <span className="font-medium">{d.label}</span> — {d.description}{' '}
                <span className="text-xs text-gray-500">(scored from the attempt's evidence, or "Not measured")</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {m.overall && (
        <p className="mt-2 text-sm text-gray-700" data-testid={`preview-overall-${m.modality}`}>
          Overall: reported only when at least {m.overall.minScoredForOverall} dimensions are scored; otherwise UNKNOWN — never an average of too little, never 0.
        </p>
      )}

      {m.observableEvidence.length > 0 && (
        <div className="mt-2">
          <h4 className="text-xs font-semibold uppercase text-gray-500">Reported beside the overall (never inside it)</h4>
          <ul className="mt-1 space-y-1 text-sm">
            {m.observableEvidence.map((o) => (
              <li key={o.key}>
                {o.label}: {o.state === 'available' ? o.evidenceClass : `UNAVAILABLE — ${o.explanation ?? ''}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export function AssessmentPreviewPanel({ preview }: { preview: AssessmentPreview }) {
  return (
    <div className="space-y-4" data-testid="assessment-preview">
      <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="preview-not-an-attempt">
        Authoring preview — not an assessment. Nothing here is an attempt: no session, result, XP, badge, portfolio item or
        marketplace signal is created, and no score is shown.
      </p>
      <div className="text-sm text-gray-700">
        <p>
          Content: {preview.contentStatus === 'complete' ? 'COMPLETE' : 'CONTENT_INCOMPLETE'}
          {!preview.validation.valid && ` — the stored progression does not validate (${preview.validation.problems.length} problem(s))`}
        </p>
        <p data-testid="preview-testing">
          Testing: {preview.testing.available ? 'available' : `unavailable${preview.testing.reason ? ` — ${preview.testing.reason}` : ''}`}
        </p>
      </div>
      {preview.modalities.map((m) => <ModalityCard key={m.modality} m={m} />)}
    </div>
  );
}
